const isDateCompleted = (now, end) => end && now > end;
const isDateInProgress = (now, start, end) =>
  start && end && now >= start && now <= end;
const isDateRegistrationClosed = (now, regEnd, start) =>
  regEnd && start && now > regEnd && now < start;
const isDateRegistrationOpenWithEnd = (now, regStart, regEnd) =>
  regStart && regEnd && now >= regStart && now <= regEnd;
const isDateRegistrationOpenNoEnd = (now, regStart, regEnd, start) =>
  regStart && !regEnd && now >= regStart && (!start || now < start);
const isDateSetup = (now, regStart) => regStart && now < regStart;

const hasAnySeasonDate = (season) => {
  return !!(
    season.registration_start_date ||
    season.start_date ||
    season.season_start_date
  );
};

export const getSeasonPhase = (season) => {
  if (!season) return "setup";

  // Prioritize explicit status if in a progress or completed state
  if (season.status === "in_progress" || season.status === "completed") {
    return season.status;
  }

  const now = new Date();

  // If no dates are set at all, completely fallback to legacy manual status if available
  if (!hasAnySeasonDate(season)) {
    return season.status || "setup";
  }

  const regStart = season.registration_start_date
    ? new Date(season.registration_start_date)
    : null;
  const regEnd = season.registration_end_date
    ? new Date(season.registration_end_date)
    : null;
  let seasonStart = null;
  if (season.start_date) {
    seasonStart = new Date(season.start_date);
  } else if (season.season_start_date) {
    seasonStart = new Date(season.season_start_date);
  }

  let seasonEnd = null;
  if (season.end_date) {
    seasonEnd = new Date(season.end_date);
  } else if (season.season_end_date) {
    seasonEnd = new Date(season.season_end_date);
  }

  if (isDateCompleted(now, seasonEnd)) return "completed";
  if (isDateInProgress(now, seasonStart, seasonEnd)) return "in_progress";
  if (isDateRegistrationClosed(now, regEnd, seasonStart))
    return "registration_closed";
  if (
    isDateRegistrationOpenWithEnd(now, regStart, regEnd) ||
    isDateRegistrationOpenNoEnd(now, regStart, regEnd, seasonStart)
  )
    return "registration";
  if (isDateSetup(now, regStart)) return "setup";

  // If we can't derive it cleanly, fallback to standard
  return season.status || "setup";
};

export const formatLocalDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatLocalTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  const datePart = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
};

export const getDaysInMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

export const getFirstDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1).getDay();

export const getAdjacentMonth = (date, offset) =>
  new Date(date.getFullYear(), date.getMonth() + offset, 1);

export const categorizeScheduleDate = (dateStr) => {
  if (!dateStr) return "Past";
  const gameDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const gameDay = new Date(gameDate);
  gameDay.setHours(0, 0, 0, 0);

  if (gameDay.getTime() >= today.getTime()) return "Upcoming";
  return "Past";
};
